import { io } from 'socket.io-client';
import { Formik, Form } from 'formik';
import { useDispatch, useSelector } from 'react-redux';
import React, { Fragment, useEffect, useState } from 'react';

import { Box, Typography } from '@mui/material';

import AIMatchingView from './AIMatchingView';
import { snackAlertData } from 'utils/constants';
import ForReviewFilters from './ForReviewFilters';
import EmailSourceModal from './EmailSourceModal';
import {
    forReviewSchema,
    forReviewInitialValues,
} from 'components/Inventory/utils/forReviewSchema';
import ForReviewAccordion from './ForReviewAccordion';
import { handleErrorAlert } from 'utils/handleErrorAlert';
import { handleSuccessAlert } from 'utils/handleSuccessAlert';
import PlaidTransactionNotesModal from './TransactionNotesModal';
import { getBankAccountsDropdown } from 'store/services/bank.Service';
import { useInventoryService } from 'store/services/inventory.service';
import { forReviewTableHeaders } from '../../utils/forReviewTableHeaders';
import { setFilters, clearFilters } from 'store/slices/inventoryFilters.slice';
import { parseForReviewFilters } from 'components/Inventory/utils/parseFilters';
import { SnackBarAlert, CircleLoading, TablePagination, DataTableWithAccordion } from 'shared';

const TOKEN = process.env.REACT_APP_SOCKET_TOKEN;
const SOCKET_URL = process.env.REACT_APP_SOCKET_URL;

const ForReview = ({}) => {
    const dispatch = useDispatch();
    const filters = useSelector((state) => state.inventoryFilter);
    const companyData = useSelector((state) => state.company.data);
    const { getInventoryForReview, getAutoMatchStatus, startAutoMatch } = useInventoryService();

    const [page, setPage] = useState(1);
    const [data, setData] = useState(null);
    const [banks, setBanks] = useState(null);
    const [selected, setSelected] = useState([]);
    const [loading, setLoading] = useState(true);
    const [sortOrder, setSortOrder] = useState('desc');
    const [transaction, setTransaction] = useState(null);
    const [tableLoading, setTableLoading] = useState(true);
    const [sorting, setSorting] = useState({ date: true });
    const [alertData, setAlertData] = useState(snackAlertData);
    const [showNotesModal, setShowNotesModal] = useState(false);
    const [autoMatchLoading, setAutoMatchLoading] = useState(true);
    const [showOnlyAIMatchView, setShowOnlyAIMatchView] = useState(false);
    const [showEmailSourceModal, setShowEmailSourceModal] = useState(false);
    const [filterValues, setFilterValues] = useState(parseForReviewFilters(filters));

    const handleGettingInventoryForReview = async (
        page,
        values,
        column = null,
        sortOrder = null,
    ) => {
        try {
            setTableLoading(true);
            setPage(page);
            const data = parseForReviewFilters(values);
            const response = await getInventoryForReview({
                page,
                companyId: companyData.id,
                sortColumn: column,
                sortOrder,
                rowsPerPage: 30,
                ...data,
            });
            setData(response);
            dispatch(setFilters(values));
        } catch (error) {
            handleErrorAlert(setAlertData);
        } finally {
            setTableLoading(false);
        }
    };

    const handleStartAutoMatch = async () => {
        try {
            setAutoMatchLoading(true);
            const response = await startAutoMatch({ company_id: companyData?.id });
            handleSuccessAlert(setAlertData, response?.detail);
        } catch (error) {
            handleErrorAlert(setAlertData);
        }
    };

    const handleGetAutoMatchStatus = async () => {
        try {
            const response = await getAutoMatchStatus(companyData?.id);
            setAutoMatchLoading(response.sync_status == 'in-progress');
        } catch (error) {
            handleErrorAlert(setAlertData);
        }
    };

    const handleGetBankAccountsDropdown = async () => {
        try {
            const response = await getBankAccountsDropdown(companyData?.id, null, 'review');
            let total_count = 0;
            const bankValues = response.data.map((bank) => {
                total_count += bank.transactions_count;
                return { label: bank.name, value: bank.id, total: bank.transactions_count };
            });
            setBanks([{ value: 'all', label: 'All accounts', total: total_count }, ...bankValues]);
        } catch (error) {
            handleErrorAlert(setAlertData);
        } finally {
            setLoading(false);
        }
    };

    const handleRemoveMatchedItem = (matchedId) => {
        setData((prevData) => {
            const newResults = prevData.results.filter((item) => item.id !== matchedId);
            const newCount = prevData.count - 1;
            const isFirstPage = page === 1;
            const shouldGoToPrevPage = newResults.length === 0 && !isFirstPage;

            if (newResults.length === 0 && isFirstPage) {
                handleGettingInventoryForReview(1, filterValues);
            } else if (shouldGoToPrevPage) {
                handlePageChange(page - 1, filterValues);
            }

            return {
                ...prevData,
                count: newCount,
                results: newResults,
            };
        });
    };

    const handlePageChange = (page, filters) => {
        setPage(page);
        setSelected([]);
        handleGettingInventoryForReview(page, filters);
    };

    const gettingInitialInventoryForReview = async (initialPage = null) => {
        const pageNumber = initialPage
            ? initialPage
            : data?.results?.length == 1 && page != 1
              ? page - 1
              : page;

        await handleGettingInventoryForReview(
            pageNumber,
            filterValues,
            Object.keys(sorting)[0],
            sortOrder,
        );
        setPage(pageNumber);
    };

    const showAccordion = (row, onClose) => {
        return (
            <ForReviewAccordion
                rowData={row}
                onClose={onClose}
                dateBuffer={filterValues.dateBuffer}
                amountBuffer={filterValues.amountBuffer}
                onMatchSuccess={handleRemoveMatchedItem}
                gettingInitialInventoryForReview={gettingInitialInventoryForReview}
            />
        );
    };

    const handleClearClick = (handleReset) => {
        setPage(1);
        handleReset();
        dispatch(clearFilters());
        setSelected([]);
        setFilterValues(forReviewInitialValues);
        handleGettingInventoryForReview(1, {});
        setSortOrder('desc');
        setSorting({ date: true });
    };

    const handleSourceClick = (rowData) => {
        setTransaction(rowData);
        setShowEmailSourceModal(true);
    };

    const sortHandler = async (columnId, newSortOrder, filters) => {
        try {
            await handleGettingInventoryForReview(page, filters, columnId, newSortOrder);
            setSorting({ [columnId]: true });
            setSortOrder(newSortOrder);
        } catch (error) {
            handleErrorAlert(setAlertData);
        }
    };

    const handleSelectAllRows = (event) => {
        if (event.target.checked) {
            const selectedRows = [];
            data?.results?.forEach((cellData) => {
                if (!selected.includes(cellData.id)) {
                    selectedRows.push(cellData.id);
                }
            });
            setSelected([...selected, ...selectedRows]);
        } else {
            setSelected([]);
        }
    };

    const handleSelectRow = (event, id) => {
        event.stopPropagation();
        if (event.target.checked) {
            setSelected([...selected, id]);
        } else {
            setSelected(selected.filter((item) => item !== id));
        }
    };

    const handleAIMatch = () => {
        setShowOnlyAIMatchView(true);
    };

    const handleNotesClick = (rowData) => {
        setTransaction(rowData);
        setShowNotesModal(true);
    };

    const handleNoteSuccess = async () => {
        await handleGettingInventoryForReview(
            page,
            filterValues,
            Object.keys(sorting)[0],
            sortOrder,
        );
    };

    useEffect(() => {
        if (companyData?.id) {
            Promise.all([
                handleGettingInventoryForReview(page, filterValues),
                handleGetBankAccountsDropdown(),
                handleGetAutoMatchStatus(),
            ]);
        }
    }, [companyData]);

    useEffect(() => {
        if (!companyData) return;

        const socket = io(SOCKET_URL, {
            transports: ['websocket'],
            path: '/api/socket/',
            query: {
                token: TOKEN,
            },
        });

        const eventName = `auto-match-status/${companyData.id}/`;

        socket.off(eventName);
        socket.on(eventName, (data) => {
            if (data.status == 'completed') {
                handleSuccessAlert(setAlertData, 'Auto matching completed successfully');
                setAutoMatchLoading(false);
                handleGettingInventoryForReview(
                    page,
                    filterValues,
                    Object.keys(sorting)[0],
                    sortOrder,
                );
            }
        });

        socket.on('connect_error', (error) => {
            console.error('Connection error:', error);
        });

        return () => {
            socket.disconnect();
        };
    }, [companyData]);

    return (
        <Fragment>
            {showOnlyAIMatchView ? (
                <AIMatchingView onExit={() => setShowOnlyAIMatchView(false)} />
            ) : loading ? (
                <CircleLoading />
            ) : (
                <Formik
                    enableReinitialize
                    initialValues={{ ...forReviewInitialValues, ...filters }}
                    validationSchema={forReviewSchema}
                    onSubmit={(values) => {
                        setSelected([]);
                        setFilterValues(values);
                        handleGettingInventoryForReview(
                            1,
                            values,
                            Object.keys(sorting)[0],
                            sortOrder,
                        );
                    }}
                >
                    {({ values, handleReset, handleChange, setFieldValue }) => {
                        return (
                            <Form>
                                <ForReviewFilters
                                    data={data}
                                    banks={banks}
                                    values={values}
                                    selected={selected}
                                    setSelected={setSelected}
                                    setAlertData={setAlertData}
                                    handleReset={handleReset}
                                    handleChange={handleChange}
                                    setFieldValue={setFieldValue}
                                    handleAIMatch={handleAIMatch}
                                    autoMatchLoading={autoMatchLoading}
                                    handleClearClick={handleClearClick}
                                    handleStartAutoMatch={handleStartAutoMatch}
                                    getInitialInventoryForReview={gettingInitialInventoryForReview}
                                />

                                {companyData?.id && data ? (
                                    <Fragment>
                                        {data?.count > 0 && (
                                            <Box
                                                display="flex"
                                                marginBottom="5px"
                                                justifyContent="flex-end"
                                            >
                                                <Typography fontSize="18px">{`TOTAL: ${data?.count} ROWS`}</Typography>
                                            </Box>
                                        )}
                                        <DataTableWithAccordion
                                            data={data}
                                            sorting={sorting}
                                            selected={selected}
                                            isSelectable={true}
                                            sortOrder={sortOrder}
                                            isLoading={tableLoading}
                                            setSelected={setSelected}
                                            showHeaderCheckBox={true}
                                            showAccordion={showAccordion}
                                            handleSelectRow={handleSelectRow}
                                            handleSelectAllRows={handleSelectAllRows}
                                            sortHandler={(columnId, newSortOrder) =>
                                                sortHandler(columnId, newSortOrder, values)
                                            }
                                            headers={forReviewTableHeaders(
                                                handleSourceClick,
                                                handleNotesClick,
                                            )}
                                        />
                                        <TablePagination
                                            page={page}
                                            rowsPerPage={30}
                                            count={data.count}
                                            onChange={(e, pageNumber) =>
                                                handlePageChange(pageNumber, values)
                                            }
                                        />
                                    </Fragment>
                                ) : (
                                    <CircleLoading />
                                )}
                            </Form>
                        );
                    }}
                </Formik>
            )}

            {showEmailSourceModal && (
                <EmailSourceModal
                    transaction={transaction}
                    showEmailSourceModal={showEmailSourceModal}
                    setShowEmailSourceModal={setShowEmailSourceModal}
                />
            )}

            {showNotesModal && (
                <PlaidTransactionNotesModal
                    transaction={transaction}
                    setAlertData={setAlertData}
                    showNotesModal={showNotesModal}
                    setShowNotesModal={setShowNotesModal}
                    handleNoteSuccess={handleNoteSuccess}
                />
            )}

            <SnackBarAlert alertData={alertData} setAlertData={setAlertData} />
        </Fragment>
    );
};

export default ForReview;
